import {
  Box,
  Card,
  Button,
  Flex,
  HStack,
  Portal,
  SimpleGrid,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { useId, useRef } from 'react'
import ReactQuill, { type QuillOptionsStatic } from 'react-quill-new'
import { LuBadgeInfo } from 'react-icons/lu'
import 'react-quill-new/dist/quill.snow.css'
import '@/styles/components/settings/MailEditor.css'
import type { MailTemplatePlaceholder } from '@/common/mail-templates.common'

interface MailEditorProps {
  value: string
  onChange: (html: string) => void
  placeholders?: MailTemplatePlaceholder[]
}

export default function MailEditor({ value, onChange, placeholders }: MailEditorProps) {
  const quillRef = useRef<ReactQuill | null>(null)
  const instanceId = useId()

  const hasPlaceholders = (placeholders || []).length > 0

  const handleInsertPlaceholder = (value: string) => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return

    const range = quill.getSelection(true)
    const parsed = `{{${value}}}`

    if (range) {
      quill.insertText(range.index, parsed)
      quill.setSelection(range.index + parsed.length, 0)
    } else {
      const length = quill.getLength()
      quill.insertText(length, parsed)
      quill.setSelection(length + parsed.length, 0)
    }
  }

  const modules: QuillOptionsStatic['modules'] = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      ['clean'],
    ],
  }

  const formats: QuillOptionsStatic['formats'] = [
    'header',
    'bold',
    'italic',
    'underline',
    'strike',
    'list',
    'bullet',
    'link',
  ]

  return (
    <SimpleGrid
      columns={{ base: 1, lg: hasPlaceholders ? 2 : 1 }}
      gap={4}
      w="100%"
      minW={0}
      alignItems="start"
    >
      <Card.Root minW={0} h="xl">
        <Card.Body>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={onChange}
            modules={modules}
            formats={formats}
            style={{ height: '100%' }}
          />
        </Card.Body>
      </Card.Root>

      {hasPlaceholders && (
        <Card.Root alignSelf="start" maxH="xl" overflowY="auto" minW={0}>
          <Card.Header>
            <HStack gap={2}>
              <Card.Title>Platzhalter einfügen</Card.Title>

              <Tooltip.Root ids={{ trigger: `${instanceId}-placeholders-info` }}>
                <Tooltip.Trigger asChild>
                  <Box as="span" cursor="help">
                    <LuBadgeInfo />
                  </Box>
                </Tooltip.Trigger>
                <Portal>
                  <Tooltip.Positioner>
                    <Tooltip.Content maxW="360px">
                      <Text>
                        Platzhalter sorgen dafür, dass Ihre Texte dynamisch an jede einzelne
                        Rechnung angepasst werden, ohne dass Sie diese manuell ändern müssen.
                      </Text>
                    </Tooltip.Content>
                  </Tooltip.Positioner>
                </Portal>
              </Tooltip.Root>
            </HStack>
          </Card.Header>

          <Card.Body>
            <Flex wrap="wrap" gap={2}>
              {placeholders!.map(ph => (
                <Button
                  key={ph.name}
                  variant="outline"
                  onClick={() => handleInsertPlaceholder(ph.name)}
                >
                  <HStack gap={2}>
                    <Text as="span">{ph.displayName}</Text>

                    <Tooltip.Root ids={{ trigger: `${instanceId}-ph-info-${ph.name}` }}>
                      <Tooltip.Trigger asChild>
                        <Box
                          as="span"
                          cursor="help"
                          onClick={e => e.stopPropagation()}
                          onMouseDown={e => e.stopPropagation()}
                        >
                          <LuBadgeInfo />
                        </Box>
                      </Tooltip.Trigger>
                      <Portal>
                        <Tooltip.Positioner>
                          <Tooltip.Content maxW="320px">
                            <VStack alignItems="start" gap={1}>
                              <Text>{ph.displayName}</Text>
                              <Text>{ph.description}</Text>
                              <Text>Beispiel: {ph.example}</Text>
                            </VStack>
                          </Tooltip.Content>
                        </Tooltip.Positioner>
                      </Portal>
                    </Tooltip.Root>
                  </HStack>
                </Button>
              ))}
            </Flex>
          </Card.Body>
        </Card.Root>
      )}
    </SimpleGrid>
  )
}
